import { ArrowRight } from "lucide-react";

import { Eyebrow } from "@/components/eyebrow";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface UnlockConfirmProps {
  /** The draft's company name, shown so the user can recognize what they're unlocking. */
  companyName: string;
  onUnlock: () => void;
  onDiscard: () => void;
}

/**
 * Explicit confirmation shown when an authenticated user has a submit-pending
 * assessment draft. Submission never fires on its own: this guards against a
 * freshly-signed-in user on a shared browser silently creating (and having
 * attributed to them) an assessment from a stranger's abandoned draft.
 */
export function UnlockConfirm({ companyName, onUnlock, onDiscard }: UnlockConfirmProps) {
  const label = companyName.trim() || "your assessment";

  return (
    <div>
      <Eyebrow>Welcome back</Eyebrow>
      <h1 className="text-primary mt-3 max-w-[20ch] text-4xl font-normal tracking-tight sm:text-5xl">
        Unlock your report.
      </h1>
      <p className="text-muted-foreground mt-4 mb-8 text-lg">
        You have a completed assessment for{" "}
        <span className="text-primary font-medium">{label}</span> ready to generate. Unlock it to
        save the report to your account.
      </p>

      <Card className="border-accent">
        <CardContent className="space-y-4 pt-6">
          <Button type="button" className="w-full" onClick={onUnlock}>
            Unlock my report <ArrowRight className="h-4 w-4" />
          </Button>
          <button
            type="button"
            onClick={onDiscard}
            className="text-muted hover:text-primary w-full text-center text-sm transition-colors"
          >
            Discard this assessment
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
